import React, { useContext, useEffect, useState } from 'react';
import GlobalRateMap from '@reuters-graphics/chart-module-globalratemap';
import LoaderPanel from 'Components/common/LoaderPanel';
import { LocaleContext, ContentContext } from 'Components/hooks/context';
import classnames from 'classnames';
import { component } from './styles.module.scss';
import debounce from 'lodash/debounce';
import { globalTopojsonURL } from 'Constants/locations';
import useWindowDimensions from 'Components/hooks/useWindowDimensions';

const RateMap = ({ globalStatistics }) => {
  const locale = useContext(LocaleContext);
  const content = useContext(ContentContext);
  const { distributionRanks } = globalStatistics;
  const { cases } = distributionRanks;
  const { width: windowWidth } = useWindowDimensions();

  const [topology, setTopology] = useState(null);

  const data = Object.keys(cases).map(country => ({
    key: country,
    value: cases[country],
  }));

  const chart = new GlobalRateMap();

  const chartContainer = React.createRef();

  const resize = debounce(() => {
    if (!chartContainer.current) return;
    chart.draw();
  }, 250);

  const fetchTopojson = () => {
    fetch(globalTopojsonURL)
      .then(res => res.json())
      .then((topology) => {
        setTopology(topology);
      });
  };

  useEffect(() => {
    if (topology) return;
    const node = chartContainer.current;
    const observerCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        fetchTopojson();
        observer.unobserve(node);
      });
    };

    const observerOptions = {
      rootMargin: windowWidth < 600 ? '2000px' : '1000px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    observer.observe(node);
    return () => {
      observer.unobserve(node);
    };
  }, [chartContainer, topology, windowWidth]);

  // Draw chart
  useEffect(() => {
    if (!topology) return;
    chart
      .selection(chartContainer.current)
      .data(data)
      .topojson(topology)
      .props({
        locale,
        map_stroke_width: 1,
        map_stroke_color: '#2f353f',
        map_fill: 'rgba(153,153,153,0.25)',
        map_stroke_color_active: 'rgba(153,153,153,0.75)',
        spike_size: 5,
        spike_height: 35,
        spike_stroke_width: 1.6,
        spike_highlight_stroke_width: 3,
        hover_gap: 18,
        height: 750,
        key: {
          text: {
            main_text: content.common.mapText.legend.mainText,
            red_peak: content.common.mapText.legend.redPeak,
            orange_peak: content.common.mapText.legend.orangePeak,
            white_peak: content.common.mapText.legend.whitePeak,
          },
        },
        at_peak_text: content.common.peak.atPeak,
        of_peak_text: content.common.mapText.ofPeak,
      })
      .draw();
  }, [chart, data, locale, chartContainer, topology]);

  // Resize chart
  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  return (
    <div className={classnames(component)}>
      <div className='global-rate-map' ref={chartContainer} />
      {!topology && (
        <LoaderPanel height={200} />
      )}
      <div className='section-spacer-internal' />
    </div>
  );
};

export default RateMap;
